import { pushLayer, removeLayer } from "./EscapeLayerStack";
import { setControllingTriggersExpanded } from "./Utils";

/**
 * Owns all dynamic accessibility state for popup scenes: focus capture/restore,
 * inert on the background scenes, aria-expanded on triggers, ESC and
 * click-outside-to-close. Static attributes (role="dialog", aria-modal,
 * aria-label, tabindex and the aria-haspopup/aria-controls trigger decoration)
 * come from the publish backend.
 *
 * SceneTransitions calls activate() from openPopup and deactivate() from
 * finishPopupClose and the popup->popup switch loop; Spread calls destroy() on
 * teardown and on layout switch.
 */
export default class PopupAccessibility {
    constructor(spread) {
        this.spread = spread;
        this._trigger = null;
        this._escToken = null;
        this._pointerDownHandler = null;
        this._clickHandler = null;
        this._pointerDownOnBackdrop = false;
        this._activeSceneElement = null;
    }

    activate(sceneElement, contentElement) {
        // idempotent: tear down any still-active popup before taking over, so a
        // re-open or an interrupted open cannot leak the previous listeners
        if (this._activeSceneElement) {
            this.deactivate(this._activeSceneElement, { restoreFocus: false });
        }

        const active = this.getDeepActiveElement();
        // keep the original trigger in the base scene on a popup->popup switch.
        // NOTE: closest() cannot cross a shadow boundary, so focus sitting inside
        // a nested web component within a popup is a known, currently-unreachable
        // edge case (popup content has no nested shadow roots today).
        if (active && !active.closest('.scene.popup_scene')) {
            this._trigger = active;
        }

        // inert removes the background from the a11y tree and traps Tab inside the popup
        this.spread.getLayoutContainer().querySelectorAll('.scene').forEach((scene) => {
            if (scene !== sceneElement) {
                scene.inert = true;
            }
        });

        this.setTriggersExpanded(sceneElement, 'true');

        this._escToken = pushLayer(() => this.spread.setActivePopup({ mode: 'close' }));

        // click-outside: listeners on the scene element itself because the shadow
        // root retargets events at document level. The pointerdown+click pair
        // ignores text-selection drags and touch scroll-pans.
        const isBackdrop = (target) => target === sceneElement || target === contentElement;
        this._pointerDownOnBackdrop = false;
        this._pointerDownHandler = (e) => {
            this._pointerDownOnBackdrop = isBackdrop(e.target);
        };
        this._clickHandler = (e) => {
            if (this._pointerDownOnBackdrop && isBackdrop(e.target)) {
                this.spread.setActivePopup({ mode: 'close' });
            }
        };
        sceneElement.addEventListener('pointerdown', this._pointerDownHandler);
        sceneElement.addEventListener('click', this._clickHandler);

        this._activeSceneElement = sceneElement;

        sceneElement.focus({ preventScroll: true });
    }

    deactivate(sceneElement, { restoreFocus = true } = {}) {
        // only the currently-active popup owns the listeners and state; the
        // openPopup switch loop calls deactivate() for every non-target popup,
        // most of which were never activated, so ignore those
        if (sceneElement !== this._activeSceneElement) {
            return;
        }

        if (this._pointerDownHandler) {
            sceneElement.removeEventListener('pointerdown', this._pointerDownHandler);
            this._pointerDownHandler = null;
        }
        if (this._clickHandler) {
            sceneElement.removeEventListener('click', this._clickHandler);
            this._clickHandler = null;
        }
        removeLayer(this._escToken);
        this._escToken = null;

        // spread-wide instead of the active layout: survives a desktop<->mobile
        // layout switch while the popup was open
        this.spread.getSpreadElement(false).querySelectorAll('.scene[inert]').forEach((scene) => {
            scene.inert = false;
        });

        this.setTriggersExpanded(sceneElement, 'false');

        this._activeSceneElement = null;

        if (restoreFocus) {
            const trigger = this._trigger;
            this._trigger = null;

            let restored = false;
            if (trigger && trigger.isConnected) {
                trigger.focus({ preventScroll: true });
                restored = this.getDeepActiveElement() === trigger;
            }
            // no trigger, or focus() did not take (e.g. hidden/unfocusable): fall
            // back to the spread element so focus never gets stranded
            if (!restored) {
                this.spread.getSpreadElement(false).focus({ preventScroll: true });
            }
        }
    }

    // called from Spread.destroy() and the layout-switch handler so an open
    // popup does not leak its listeners or its EscapeLayerStack entry
    destroy() {
        if (this._activeSceneElement) {
            this.deactivate(this._activeSceneElement, { restoreFocus: false });
        }
    }

    setTriggersExpanded(sceneElement, value) {
        // trigger decoration comes from the publish backend; on older publishes
        // this selector matches nothing and the toggle is a safe no-op
        setControllingTriggersExpanded(this.spread.getSpreadElement(false), sceneElement.id, value);
    }

    getDeepActiveElement() {
        let el = document.activeElement;
        while (el?.shadowRoot?.activeElement) {
            el = el.shadowRoot.activeElement;
        }
        return el;
    }
}
